import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Section, FieldLabel } from './FormPrimitives';
import {
  LOSS_TYPE_KEYS, LOSS_TYPE_LABELS,
  formatCurrency, parseCurrency, formatPhone, parsePhone,
} from '../../utils/claimHelpers';

/**
 * All form-field sections of the claim panel.
 * Renders per-loss-type cards (status/submittal/amounts/in-house/insurance),
 * then company + date, driver + vehicle, location + description,
 * loss-type checkboxes, other vehicle, fault/driver charged, police, and
 * injury/tow-away/fatality/DOT-recordable.
 *
 * Receives all state via props from the parent EditClaimPanel.
 */
export default function ClaimForm({
  formData, originalData, editMode, lossType,
  companies, policyMatches, hoverTimers,
  handleChange, handleTypeChange,
}) {
  const ro = !editMode;
  const inputBg = ro ? 'bg-gray-100' : 'bg-white';
  const activeTypes = lossType ? [lossType] : LOSS_TYPE_KEYS.filter(k => formData[k]);

  return (
    <>
      {/* Per-loss-type sections */}
      {activeTypes.map(tk => {
        const td = formData.type_data?.[tk] || {};
        return (
          <div key={tk} className="space-y-2.5 pt-1" data-testid={`loss-type-section-${tk}`}>
            <div className="text-xs font-bold text-blue-600 uppercase tracking-wide border-b border-blue-200 pb-1">
              {LOSS_TYPE_LABELS[tk]}
            </div>

            {td.submittal === 'Insurance' && formData.fault === 'At Fault' && policyMatches[tk] ? (
              <div className="px-2.5 py-1.5 rounded bg-teal-50 border border-teal-200 text-xs" data-testid={`policy-match-${tk}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-teal-800">{policyMatches[tk].carrier_name || 'Carrier N/A'}</span>
                  <span className="text-teal-600">#{policyMatches[tk].policy_no}</span>
                  <span className="text-teal-500">({policyMatches[tk].start_date} — {policyMatches[tk].end_date})</span>
                </div>
              </div>
            ) : td.submittal === 'Insurance' && formData.fault === 'At Fault' && !policyMatches[tk] && formData[tk] ? (
              <div className="px-2.5 py-1.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-700" data-testid={`policy-warning-${tk}`}>
                No matching policy for date of loss
              </div>
            ) : null}

            {/* Status + Submittal */}
            <Section>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Status</FieldLabel>
                  <Select value={td.status || 'New'} onValueChange={(v) => handleTypeChange(tk, 'status', v)} disabled={ro}>
                    <SelectTrigger className={`w-full h-9 text-sm ${inputBg}`} data-testid={`edit-status-${tk}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {['New', 'In Process', 'Closed'].map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {editMode && td.status === 'Closed' && (td.submittal === 'Pending' || !td.submittal) && (
                    <p className="text-xs text-red-500 mt-1" data-testid={`status-closed-error-${tk}`}>Submittal cannot be Pending when Status is Closed</p>
                  )}
                </div>
                <div>
                  <FieldLabel>Submittal</FieldLabel>
                  <Select value={td.submittal || 'Pending'} onValueChange={(v) => handleTypeChange(tk, 'submittal', v)} disabled={ro}>
                    <SelectTrigger className={`w-full h-9 text-sm ${inputBg}`} data-testid={`edit-submittal-${tk}`}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {['In-House', 'Insurance', 'Pending', 'NA'].filter(s => !(s === 'In-House' && (tk === 'ocac' || tk === 'wc'))).map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Section>

            {/* Amounts */}
            <Section>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Initial Amount</FieldLabel>
                  <Input type="text" placeholder="$0.00" value={editMode ? (td.initial_amount || '') : formatCurrency(td.initial_amount)}
                    onChange={(e) => handleTypeChange(tk, 'initial_amount', e.target.value)} readOnly={ro}
                    className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg}`} data-testid={`edit-initial-amount-${tk}`} />
                </div>
                <div>
                  <FieldLabel required={td.status === 'Closed'}>Final Amount</FieldLabel>
                  <Input type="text" placeholder="$0.00" value={editMode ? (td.final_amount || '') : formatCurrency(td.final_amount)}
                    onChange={(e) => handleTypeChange(tk, 'final_amount', e.target.value)} readOnly={ro}
                    className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && td.status === 'Closed' && !td.final_amount?.toString().trim() ? 'border-red-400' : ''}`}
                    data-testid={`edit-final-amount-${tk}`} />
                </div>
              </div>
            </Section>

            {/* In-House Details */}
            {td.submittal === 'In-House' && (
              <div className="mt-2">
                <Section>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel required>Other Party</FieldLabel>
                        <Input type="text" placeholder="Name" value={td.other_party || ''}
                          onChange={(e) => handleTypeChange(tk, 'other_party', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.other_party?.trim() ? 'border-red-400' : ''}`}
                          data-testid={`edit-other-party-${tk}`} />
                      </div>
                      <div>
                        <FieldLabel required>In-House Claim #</FieldLabel>
                        <Input type="text" placeholder="Claim #" value={td.in_house_claim_no || ''}
                          onChange={(e) => handleTypeChange(tk, 'in_house_claim_no', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.in_house_claim_no?.trim() ? 'border-red-400' : ''}`}
                          data-testid={`edit-in-house-claim-${tk}`} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel required>Contact</FieldLabel>
                      <Input type="text" placeholder="Name" value={td.contact || ''}
                        onChange={(e) => handleTypeChange(tk, 'contact', e.target.value)} readOnly={ro}
                        className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.contact?.trim() ? 'border-red-400' : ''}`}
                        data-testid={`edit-contact-${tk}`} />
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-3">
                      <div>
                        <FieldLabel required>Contact Phone</FieldLabel>
                        <Input type="tel" placeholder="(555) 555-5555" value={formatPhone(td.contact_phone)}
                          onChange={(e) => handleTypeChange(tk, 'contact_phone', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.contact_phone?.trim() ? 'border-red-400' : ''}`}
                          maxLength={14} data-testid={`edit-contact-phone-${tk}`} />
                      </div>
                      <div className="w-24">
                        <FieldLabel>Extension</FieldLabel>
                        <Input type="text" placeholder="Ext" value={td.extension || ''}
                          onChange={(e) => handleTypeChange(tk, 'extension', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg}`} data-testid={`edit-extension-${tk}`} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel required>Contact Email</FieldLabel>
                      <Input type="email" placeholder="Email" value={td.contact_email || ''}
                        onChange={(e) => handleTypeChange(tk, 'contact_email', e.target.value)} readOnly={ro}
                        className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.contact_email?.trim() ? 'border-red-400' : ''}`}
                        data-testid={`edit-contact-email-${tk}`} />
                    </div>
                  </div>
                </Section>
              </div>
            )}

            {/* Insurance Details */}
            {td.submittal === 'Insurance' && (
              <div className="mt-2">
                <Section>
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel required>Insurance Company</FieldLabel>
                        <Input type="text" placeholder="Name" value={td.insurance_company || ''}
                          onChange={(e) => handleTypeChange(tk, 'insurance_company', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.insurance_company?.trim() ? 'border-red-400' : ''}`}
                          data-testid={`edit-insurance-company-${tk}`} />
                      </div>
                      <div>
                        <FieldLabel required>Insurance Claim #</FieldLabel>
                        <Input type="text" placeholder="Claim #" value={td.insurance_claim_no || ''}
                          onChange={(e) => handleTypeChange(tk, 'insurance_claim_no', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.insurance_claim_no?.trim() ? 'border-red-400' : ''}`}
                          data-testid={`edit-insurance-claim-${tk}`} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel required>Adjuster</FieldLabel>
                      <Input type="text" placeholder="Name" value={td.adjuster || ''}
                        onChange={(e) => handleTypeChange(tk, 'adjuster', e.target.value)} readOnly={ro}
                        className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.adjuster?.trim() ? 'border-red-400' : ''}`}
                        data-testid={`edit-adjuster-${tk}`} />
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-3">
                      <div>
                        <FieldLabel required>Adjusters Phone</FieldLabel>
                        <Input type="tel" placeholder="(555) 555-5555" value={formatPhone(td.adjusters_phone)}
                          onChange={(e) => handleTypeChange(tk, 'adjusters_phone', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.adjusters_phone?.trim() ? 'border-red-400' : ''}`}
                          maxLength={14} data-testid={`edit-adjusters-phone-${tk}`} />
                      </div>
                      <div className="w-24">
                        <FieldLabel>Extension</FieldLabel>
                        <Input type="text" placeholder="Ext" value={td.adjusters_extension || ''}
                          onChange={(e) => handleTypeChange(tk, 'adjusters_extension', e.target.value)} readOnly={ro}
                          className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg}`} data-testid={`edit-adjusters-extension-${tk}`} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel required>Adjusters Email</FieldLabel>
                      <Input type="email" placeholder="Email" value={td.adjusters_email || ''}
                        onChange={(e) => handleTypeChange(tk, 'adjusters_email', e.target.value)} readOnly={ro}
                        className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg} ${editMode && !td.adjusters_email?.trim() ? 'border-red-400' : ''}`}
                        data-testid={`edit-adjusters-email-${tk}`} />
                    </div>
                  </div>
                </Section>
              </div>
            )}
          </div>
        );
      })}

      {/* Company Name + Date Of Loss */}
      <Section>
        <div className="space-y-2">
          <div>
            <FieldLabel>Company Name</FieldLabel>
            <Select value={formData.company_name} onValueChange={(v) => handleChange('company_name', v)} disabled={ro}>
              <SelectTrigger className={`w-full h-9 text-sm ${inputBg}`} data-testid="edit-company-select">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (<SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <FieldLabel>Date Of Loss</FieldLabel>
            <Input type="date" value={formData.date_of_loss}
              onChange={(e) => handleChange('date_of_loss', e.target.value)} readOnly={ro}
              className={`w-full h-9 text-sm ${inputBg}`} data-testid="edit-date-of-loss" />
          </div>
        </div>
      </Section>

      {/* Driver + Vehicle */}
      <Section>
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Drivers Name</FieldLabel>
              <Input type="text" value={formData.drivers_name}
                onChange={(e) => handleChange('drivers_name', e.target.value)} readOnly={ro}
                className={`w-full h-9 text-sm ${inputBg}`} data-testid="edit-driver-name" />
            </div>
            <div>
              <FieldLabel>Drivers Phone</FieldLabel>
              <Input type="tel" placeholder="(555) 555-5555" value={formatPhone(formData.drivers_ph_no)}
                onChange={(e) => handleChange('drivers_ph_no', parsePhone(e.target.value))} readOnly={ro}
                className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg}`}
                maxLength={14} data-testid="edit-driver-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Truck</FieldLabel>
              <Input type="text" value={formData.truck_no}
                onChange={(e) => handleChange('truck_no', e.target.value)} readOnly={ro}
                className={`w-full h-9 text-sm ${inputBg}`} data-testid="edit-truck-no" />
            </div>
            <div>
              <FieldLabel>Trailer</FieldLabel>
              <Input type="text" placeholder="Trailer #" value={formData.trailer_no}
                onChange={(e) => handleChange('trailer_no', e.target.value)} readOnly={ro}
                className={`w-full h-9 text-sm placeholder:italic placeholder:text-gray-400 ${inputBg}`}
                data-testid="edit-trailer-no" />
            </div>
          </div>
        </div>
      </Section>

      {/* Location + Description */}
      <Section>
        <div className="space-y-2">
          <div>
            <FieldLabel>Location</FieldLabel>
            <Input type="text" value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)} readOnly={ro}
              className={`w-full h-9 text-sm ${inputBg}`} data-testid="edit-location" />
          </div>
          <div>
            <FieldLabel>Description</FieldLabel>
            <Textarea value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)} readOnly={ro}
              rows={4} className={`w-full text-sm ${ro ? 'resize-none' : 'resize-y'} ${inputBg}`}
              data-testid="edit-description" />
          </div>
        </div>
      </Section>

      {/* Loss Type Checkboxes */}
      <Section>
        <div className="flex items-center gap-5">
          {[
            { key: 'al', label: 'AL' },
            { key: 'pd', label: 'PD' },
            { key: 'cd', label: 'CD' },
            { key: 'ocac', label: 'OCAC' },
            { key: 'wc', label: 'WC' },
          ].map(({ key, label }) => (
            <div key={key} className="flex items-center space-x-1.5"
              onMouseEnter={lossType && !ro ? () => {
                hoverTimers.current[key] = setTimeout(() => {
                  if (formData[key] && originalData[key]) {
                    toast.error("Active claim can't be unselected here. Open claim in 'All Entries' to make changes!", { id: `lock-${key}` });
                  } else if (!formData[key]) {
                    toast.info(`Selecting '${label}' will create New Claim in ${LOSS_TYPE_LABELS[key]}!`, { id: `info-${key}` });
                  }
                }, 500);
              } : undefined}
              onMouseLeave={() => { clearTimeout(hoverTimers.current[key]); }}>
              <Checkbox id={`edit-${key}`} checked={formData[key]}
                onCheckedChange={ro ? undefined : lossType
                  ? (checked) => { if (!checked && originalData[key]) return; handleChange(key, checked); }
                  : (checked) => handleChange(key, checked)}
                disabled={ro} className="w-5 h-5 border-2" data-testid={`edit-loss-${key}`} />
              <Label htmlFor={`edit-${key}`} className="text-sm text-gray-700 cursor-pointer font-medium">{label}</Label>
            </div>
          ))}
        </div>
      </Section>

      {/* Other Vehicle Involved */}
      <Section>
        <FieldLabel>Other Vehicle Involved</FieldLabel>
        <RadioGroup value={formData.other_vehicle_involved}
          onValueChange={ro ? undefined : (v) => handleChange('other_vehicle_involved', v)} disabled={ro}>
          <div className="flex gap-5 mt-1">
            {['CMV', 'NA', 'Non-CMV'].map((val) => (
              <div key={val} className="flex items-center space-x-1.5">
                <RadioGroupItem value={val} id={`edit-ov-${val}`} className="w-4 h-4" data-testid={`edit-ov-${val.toLowerCase()}`} />
                <Label htmlFor={`edit-ov-${val}`} className={`text-sm cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>{val}</Label>
              </div>
            ))}
          </div>
        </RadioGroup>
      </Section>

      {/* Fault + Driver Charged */}
      <Section>
        <div className="flex gap-10 items-start">
          <div>
            <FieldLabel>Fault</FieldLabel>
            <RadioGroup value={formData.fault}
              onValueChange={ro ? undefined : (v) => {
                handleChange('fault', v);
                if (v !== 'At Fault') { handleChange('driver_charged', ''); handleChange('deductible', false); handleChange('deductible_amount', ''); handleChange('other_amount_checked', false); handleChange('other_amount', ''); }
              }} disabled={ro}>
              <div className="flex gap-5 mt-2">
                {['At Fault', 'Not At Fault', 'Unknown'].map((val) => (
                  <div key={val} className="flex items-center space-x-1.5">
                    <RadioGroupItem value={val} id={`edit-fault-${val}`} className="w-4 h-4"
                      data-testid={`edit-fault-${val.toLowerCase().replace(/ /g, '-')}`} />
                    <Label htmlFor={`edit-fault-${val}`} className={`text-sm cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>{val}</Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>
          {formData.fault === 'At Fault' && (
            <div>
              <FieldLabel>Driver Charged</FieldLabel>
              <RadioGroup value={formData.driver_charged} onValueChange={(v) => {
                handleChange('driver_charged', v);
                if (v !== 'Yes') { handleChange('deductible', false); handleChange('deductible_amount', ''); handleChange('other_amount_checked', false); handleChange('other_amount', ''); }
              }} className="flex items-center gap-3 mt-2.5" disabled={ro}>
                <div className="flex items-center space-x-1">
                  <RadioGroupItem value="Yes" id="dc-yes" data-testid="driver-charged-yes" />
                  <Label htmlFor="dc-yes" className={`text-sm cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>Yes</Label>
                </div>
                <div className="flex items-center space-x-1">
                  <RadioGroupItem value="No" id="dc-no" data-testid="driver-charged-no" />
                  <Label htmlFor="dc-no" className={`text-sm cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>No</Label>
                </div>
              </RadioGroup>
              {formData.driver_charged === 'Yes' && (
                <div className="flex flex-col gap-1.5 mt-2">
                  {!formData.other_amount_checked && (
                    <div>
                      <div className="flex items-center gap-1.5">
                        <Checkbox id="deductible-cb" checked={formData.deductible}
                          onCheckedChange={(v) => { handleChange('deductible', !!v); if (!v) handleChange('deductible_amount', ''); }}
                          disabled={ro} data-testid="deductible-checkbox" className="w-3.5 h-3.5" />
                        <Label htmlFor="deductible-cb" className={`text-xs cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>Deductible</Label>
                      </div>
                      {formData.deductible && (
                        <Input type="text" placeholder="$0.00" value={formData.deductible_amount}
                          onChange={(e) => handleChange('deductible_amount', parseCurrency(e.target.value))}
                          readOnly={ro} className={`w-28 h-7 text-xs mt-1 ml-5 ${inputBg}`} data-testid="deductible-amount" />
                      )}
                    </div>
                  )}
                  {!formData.deductible && (
                    <div>
                      <div className="flex items-center gap-1.5">
                        <Checkbox id="other-amt-cb" checked={formData.other_amount_checked}
                          onCheckedChange={(v) => { handleChange('other_amount_checked', !!v); if (!v) handleChange('other_amount', ''); }}
                          disabled={ro} data-testid="other-amount-checkbox" className="w-3.5 h-3.5" />
                        <Label htmlFor="other-amt-cb" className={`text-xs cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>Other amount</Label>
                      </div>
                      {formData.other_amount_checked && (
                        <Input type="text" placeholder="$0.00" value={formData.other_amount}
                          onChange={(e) => handleChange('other_amount', parseCurrency(e.target.value))}
                          readOnly={ro} className={`w-28 h-7 text-xs mt-1 ml-5 ${inputBg}`} data-testid="other-amount-value" />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </Section>

      {/* Police Involved */}
      <Section>
        <div className={formData.police_involved === 'Yes' ? 'flex gap-8 items-start' : ''}>
          <div className={formData.police_involved === 'Yes' ? 'shrink-0' : ''}>
            <FieldLabel>Police Involved</FieldLabel>
            <RadioGroup value={formData.police_involved}
              onValueChange={ro ? undefined : (v) => handleChange('police_involved', v)} disabled={ro}>
              <div className="flex gap-3 mt-1">
                {['No', 'Yes'].map((val) => (
                  <div key={val} className="flex items-center space-x-1">
                    <RadioGroupItem value={val} id={`edit-police-${val.toLowerCase()}`} className="w-4 h-4"
                      data-testid={`edit-police-${val.toLowerCase()}`} />
                    <Label htmlFor={`edit-police-${val.toLowerCase()}`} className={`text-xs cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>{val}</Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          </div>
          {formData.police_involved === 'Yes' && (
            <>
              <div className="shrink-0">
                <FieldLabel required>Citation Issued</FieldLabel>
                <RadioGroup value={formData.citation}
                  onValueChange={ro ? undefined : (v) => handleChange('citation', v)} disabled={ro}>
                  <div className="flex gap-3 mt-1">
                    {['No', 'Yes'].map((val) => (
                      <div key={val} className="flex items-center space-x-1">
                        <RadioGroupItem value={val} id={`edit-citation-${val.toLowerCase()}`} className="w-4 h-4"
                          data-testid={`edit-citation-${val.toLowerCase()}`} />
                        <Label htmlFor={`edit-citation-${val.toLowerCase()}`} className={`text-xs cursor-pointer ${ro ? 'text-gray-400' : 'text-gray-700'}`}>{val}</Label>
                      </div>
                    ))}
                  </div>
                </RadioGroup>
              </div>
              <div className="flex-1 min-w-0 max-w-[45%]">
                <FieldLabel required={!formData.police_report_not_ready}>Police Report #</FieldLabel>
                <div className="flex items-center gap-2">
                  <Input type="text" placeholder="Report #" value={formData.police_report_no}
                    onChange={(e) => handleChange('police_report_no', e.target.value)} readOnly={ro}
                    disabled={formData.police_report_not_ready}
                    className={`w-full h-8 text-xs placeholder:italic placeholder:text-gray-400 ${inputBg} ${formData.police_report_not_ready ? 'opacity-50' : ''} ${editMode && !formData.police_report_not_ready && !formData.police_report_no?.trim() ? 'border-red-400' : ''}`}
                    data-testid="edit-police-report" />
                  {!formData.police_report_no?.trim() && (
                    <div className="flex items-center space-x-1 shrink-0">
                      <Checkbox id="edit-police-not-ready" checked={formData.police_report_not_ready}
                        onCheckedChange={ro ? undefined : (checked) => handleChange('police_report_not_ready', checked)}
                        disabled={ro} className="w-4 h-4 border-2" data-testid="edit-police-report-not-ready" />
                      <Label htmlFor="edit-police-not-ready" className="text-xs text-gray-500 cursor-pointer whitespace-nowrap">Not ready</Label>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </Section>

      {/* Injury | Tow Away | Fatality | DOT Recordable */}
      <Section>
        <div className="grid grid-cols-4 gap-2 md:gap-3">
          {[
            { field: 'injury', label: 'Injury' },
            { field: 'tow_away', label: 'Tow Away' },
            { field: 'fatality', label: 'Fatality' },
            { field: 'dot_recordable', label: 'DOT Recordable' },
          ].map(({ field, label }) => (
            <div key={field}
              onMouseEnter={lossType && !ro ? () => {
                hoverTimers.current[field] = setTimeout(() => {
                  toast.info(`${formData[field] ? 'Uncheck' : 'Check'} '${label}' for this claim`, { id: `info-${field}` });
                }, 500);
              } : undefined}
              onMouseLeave={() => { clearTimeout(hoverTimers.current[field]); }}>
              <FieldLabel><span className="whitespace-nowrap text-xs md:text-sm">{label}</span></FieldLabel>
              <Checkbox id={`edit-${field}`} checked={formData[field]}
                onCheckedChange={ro ? undefined : (checked) => handleChange(field, checked)}
                disabled={ro} className="w-5 h-5 border-2 mt-1" data-testid={`edit-${field}`} />
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
